import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  Lock, Plus, Trash2, Edit, Save, LogOut, 
  GripVertical, ChevronUp, ChevronDown, RefreshCw,
  Image as ImageIcon, FileText, HelpCircle, ShoppingBag, Sparkles
} from "lucide-react";
import SEO from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [activeTab, setActiveTab] = useState("flashs");
  
  // Data states
  const [flashs, setFlashs] = useState([]);
  const [products, setProducts] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [faq, setFaq] = useState([]);
  const [siteContent, setSiteContent] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  
  // Edit dialogs
  const [editingFlash, setEditingFlash] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  const [newFlashDialog, setNewFlashDialog] = useState(false);
  const [newProductDialog, setNewProductDialog] = useState(false);
  const [newPortfolioDialog, setNewPortfolioDialog] = useState(false);
  const [newFaqDialog, setNewFaqDialog] = useState(false);

  // Check session storage for auth
  useEffect(() => {
    const auth = sessionStorage.getItem("nymphea_admin_auth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, loginForm);
      setIsAuthenticated(true);
      sessionStorage.setItem("nymphea_admin_auth", "true");
      toast.success("Connexion réussie");
    } catch (error) {
      toast.error("Identifiants incorrects");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("nymphea_admin_auth");
    setLoginForm({ username: "", password: "" });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [flashsRes, productsRes, portfolioRes, faqRes, contentRes] = await Promise.all([
        axios.get(`${API}/flashs`),
        axios.get(`${API}/products`),
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/faq`),
        axios.get(`${API}/site-content`),
      ]);
      setFlashs(flashsRes.data);
      setProducts(productsRes.data);
      setPortfolio(portfolioRes.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
      setFaq(faqRes.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
      setSiteContent(contentRes.data);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // ==================== FLASH MANAGEMENT ====================
  const [flashForm, setFlashForm] = useState({
    titre: "",
    image_url: "",
    prix: "",
    taille_min: "10",
    unique: true,
    disponible: true,
  });

  const handleCreateFlash = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/flashs`, {
        ...flashForm,
        prix: parseFloat(flashForm.prix),
        taille_min: parseInt(flashForm.taille_min),
      });
      toast.success("Flash créé");
      setNewFlashDialog(false);
      setFlashForm({ titre: "", image_url: "", prix: "", taille_min: "10", unique: true, disponible: true });
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const handleUpdateFlash = async (flash) => {
    try {
      await axios.patch(`${API}/flashs/${flash.id}`, flash);
      toast.success("Flash mis à jour");
      setEditingFlash(null);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteFlash = async (id) => {
    if (!window.confirm("Supprimer ce flash ?")) return;
    try {
      await axios.delete(`${API}/flashs/${id}`);
      toast.success("Flash supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // ==================== PRODUCT MANAGEMENT ====================
  const [productForm, setProductForm] = useState({
    nom: "",
    description: "",
    image_url: "",
    prix: "",
    categorie: "prints",
    stock: 10,
  });

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products`, {
        ...productForm,
        prix: parseFloat(productForm.prix),
        stock: parseInt(productForm.stock),
      });
      toast.success("Produit créé");
      setNewProductDialog(false);
      setProductForm({ nom: "", description: "", image_url: "", prix: "", categorie: "prints", stock: 10 });
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const handleUpdateProduct = async (product) => {
    try {
      await axios.patch(`${API}/products/${product.id}`, product);
      toast.success("Produit mis à jour");
      setEditingProduct(null);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success("Produit supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // ==================== PORTFOLIO MANAGEMENT ====================
  const [portfolioForm, setPortfolioForm] = useState({
    image_url: "",
    titre: "",
    categorie: "",
  });

  const handleCreatePortfolio = async (e) => {
    e.preventDefault();
    try {
      const newOrder = portfolio.length;
      await axios.post(`${API}/portfolio`, { ...portfolioForm, order: newOrder });
      toast.success("Image ajoutée au portfolio");
      setNewPortfolioDialog(false);
      setPortfolioForm({ image_url: "", titre: "", categorie: "" });
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDeletePortfolio = async (id) => {
    if (!window.confirm("Supprimer cette image ?")) return;
    try {
      await axios.delete(`${API}/portfolio/${id}`);
      toast.success("Image supprimée");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const movePortfolioItem = async (index, direction) => {
    const newPortfolio = [...portfolio];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newPortfolio.length) return;
    
    [newPortfolio[index], newPortfolio[targetIndex]] = [newPortfolio[targetIndex], newPortfolio[index]];
    
    const orderList = newPortfolio.map((item, idx) => ({ id: item.id, order: idx }));
    
    try {
      await axios.post(`${API}/portfolio/reorder`, { order: orderList });
      setPortfolio(newPortfolio);
      toast.success("Ordre mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la réorganisation");
    }
  };

  // ==================== FAQ MANAGEMENT ====================
  const [faqForm, setFaqForm] = useState({
    question: "",
    answer: "",
  });

  const handleCreateFaq = async (e) => {
    e.preventDefault();
    try {
      const newOrder = faq.length;
      await axios.post(`${API}/faq`, { ...faqForm, order: newOrder });
      toast.success("Question ajoutée");
      setNewFaqDialog(false);
      setFaqForm({ question: "", answer: "" });
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleUpdateFaq = async (item) => {
    try {
      await axios.patch(`${API}/faq/${item.id}`, item);
      toast.success("Question mise à jour");
      setEditingFaq(null);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteFaq = async (id) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    try {
      await axios.delete(`${API}/faq/${id}`);
      toast.success("Question supprimée");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const moveFaqItem = async (index, direction) => {
    const newFaq = [...faq];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newFaq.length) return;
    
    [newFaq[index], newFaq[targetIndex]] = [newFaq[targetIndex], newFaq[index]];
    
    const orderList = newFaq.map((item, idx) => ({ id: item.id, order: idx }));
    
    try {
      await axios.post(`${API}/faq/reorder`, { order: orderList });
      setFaq(newFaq);
      toast.success("Ordre mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la réorganisation");
    }
  };

  // ==================== SITE CONTENT MANAGEMENT ====================
  const handleUpdateContent = async (content) => {
    try {
      await axios.patch(`${API}/site-content/${content.id}`, content);
      toast.success("Texte mis à jour");
      setEditingContent(null);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getPageLabel = (page) => {
    const labels = {
      home: "Accueil",
      about: "À propos",
      portfolio: "Portfolio",
      flashs: "Flashs",
      shop: "Boutique",
      contact: "Contact",
      strass: "Strass dentaire",
    };
    return labels[page] || page;
  };

  const getSectionLabel = (section) => {
    const labels = {
      hero: "En-tête principal",
      about: "Section À propos",
      intro: "Introduction",
      atelier: "L'Atelier",
      header: "En-tête de page",
    };
    return labels[section] || section;
  };

  // ==================== LOGIN SCREEN ====================
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="admin-login">
        <SEO title="Admin" />
        <div className="w-full max-w-sm p-8 border border-border/40">
          <div className="text-center mb-8">
            <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
            <h1 className="text-2xl font-heading">Administration</h1>
            <p className="text-sm text-muted-foreground mt-2">Accès réservé</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="username">Identifiant</Label>
              <Input
                id="username"
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="mt-1"
                placeholder="Votre identifiant"
                data-testid="admin-username"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="mt-1"
                placeholder="••••••••"
                data-testid="admin-password"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="admin-login-btn">
              Connexion
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ==================== ADMIN DASHBOARD ====================
  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <SEO title="Admin" />
      
      {/* Header */}
      <div className="border-b border-border/40 bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-heading">Administration Nymphea</h1>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 flex-wrap h-auto gap-1">
            <TabsTrigger value="flashs" className="gap-2">
              <Sparkles className="w-4 h-4" />
              Flashs
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="gap-2">
              <ImageIcon className="w-4 h-4" />
              Portfolio
            </TabsTrigger>
            <TabsTrigger value="products" className="gap-2">
              <ShoppingBag className="w-4 h-4" />
              Shop
            </TabsTrigger>
            <TabsTrigger value="content" className="gap-2">
              <FileText className="w-4 h-4" />
              Textes
            </TabsTrigger>
            <TabsTrigger value="faq" className="gap-2">
              <HelpCircle className="w-4 h-4" />
              FAQ
            </TabsTrigger>
          </TabsList>

          {/* ==================== FLASHS TAB ==================== */}
          <TabsContent value="flashs">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-heading">Gestion des Flashs ({flashs.length})</h2>
              <Dialog open={newFlashDialog} onOpenChange={setNewFlashDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-flash-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau Flash
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Créer un nouveau flash</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateFlash} className="space-y-4 mt-4">
                    <div>
                      <Label>Titre / Référence</Label>
                      <Input
                        value={flashForm.titre}
                        onChange={(e) => setFlashForm({ ...flashForm, titre: e.target.value })}
                        required
                        placeholder="Ex: 001a, Rose délicate..."
                      />
                    </div>
                    <div>
                      <Label>URL de l'image</Label>
                      <Input
                        value={flashForm.image_url}
                        onChange={(e) => setFlashForm({ ...flashForm, image_url: e.target.value })}
                        required
                        placeholder="https://i.ibb.co/..."
                      />
                      {flashForm.image_url && (
                        <img src={flashForm.image_url} alt="Preview" className="mt-2 h-32 object-cover border" />
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Prix (€)</Label>
                        <Input
                          type="number"
                          value={flashForm.prix}
                          onChange={(e) => setFlashForm({ ...flashForm, prix: e.target.value })}
                          required
                          min="0"
                        />
                      </div>
                      <div>
                        <Label>Taille min (cm)</Label>
                        <Input
                          type="number"
                          value={flashForm.taille_min}
                          onChange={(e) => setFlashForm({ ...flashForm, taille_min: e.target.value })}
                          required
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={flashForm.unique}
                          onCheckedChange={(checked) => setFlashForm({ ...flashForm, unique: checked })}
                        />
                        <Label>Pièce unique</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={flashForm.disponible}
                          onCheckedChange={(checked) => setFlashForm({ ...flashForm, disponible: checked })}
                        />
                        <Label>Disponible</Label>
                      </div>
                    </div>
                    <Button type="submit" className="w-full">Créer le flash</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {flashs.map((flash) => (
                <div key={flash.id} className="border border-border/40 p-4 bg-card" data-testid={`flash-item-${flash.id}`}>
                  <img
                    src={flash.image_url}
                    alt={flash.titre}
                    className="w-full h-40 object-cover mb-4"
                    onError={(e) => { e.target.src = 'https://placehold.co/400x400?text=Image+manquante'; }}
                  />
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium">{flash.titre}</h3>
                      <p className="text-sm text-muted-foreground">{flash.prix}€ - Min. {flash.taille_min} cm</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className={`text-xs px-2 py-1 text-center ${flash.disponible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {flash.disponible ? 'Disponible' : 'Réservé'}
                      </span>
                      {flash.unique && (
                        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 text-center">
                          Unique
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingFlash(flash)}
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteFlash(flash.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {flashs.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                Aucun flash. Cliquez sur "Nouveau Flash" pour en ajouter.
              </div>
            )}

            {/* Edit Flash Dialog */}
            <Dialog open={!!editingFlash} onOpenChange={() => setEditingFlash(null)}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Modifier le flash</DialogTitle>
                </DialogHeader>
                {editingFlash && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Titre / Référence</Label>
                      <Input
                        value={editingFlash.titre}
                        onChange={(e) => setEditingFlash({ ...editingFlash, titre: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>URL de l'image</Label>
                      <Input
                        value={editingFlash.image_url}
                        onChange={(e) => setEditingFlash({ ...editingFlash, image_url: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Prix (€)</Label>
                        <Input
                          type="number"
                          value={editingFlash.prix}
                          onChange={(e) => setEditingFlash({ ...editingFlash, prix: parseFloat(e.target.value) })}
                        />
                      </div>
                      <div>
                        <Label>Taille min (cm)</Label>
                        <Input
                          type="number"
                          value={editingFlash.taille_min}
                          onChange={(e) => setEditingFlash({ ...editingFlash, taille_min: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={editingFlash.unique}
                          onCheckedChange={(checked) => setEditingFlash({ ...editingFlash, unique: checked })}
                        />
                        <Label>Pièce unique</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={editingFlash.disponible}
                          onCheckedChange={(checked) => setEditingFlash({ ...editingFlash, disponible: checked })}
                        />
                        <Label>Disponible</Label>
                      </div>
                    </div>
                    <Button onClick={() => handleUpdateFlash(editingFlash)} className="w-full">
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer les modifications
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ==================== PORTFOLIO TAB ==================== */}
          <TabsContent value="portfolio">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-heading">Gestion du Portfolio ({portfolio.length})</h2>
              <Dialog open={newPortfolioDialog} onOpenChange={setNewPortfolioDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-portfolio-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter une image
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Ajouter une image au portfolio</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePortfolio} className="space-y-4 mt-4">
                    <div>
                      <Label>URL de l'image</Label>
                      <Input
                        value={portfolioForm.image_url}
                        onChange={(e) => setPortfolioForm({ ...portfolioForm, image_url: e.target.value })}
                        required
                        placeholder="https://..."
                      />
                      {portfolioForm.image_url && (
                        <img src={portfolioForm.image_url} alt="Preview" className="mt-2 h-32 object-cover border" />
                      )}
                    </div>
                    <div>
                      <Label>Titre (optionnel)</Label>
                      <Input
                        value={portfolioForm.titre}
                        onChange={(e) => setPortfolioForm({ ...portfolioForm, titre: e.target.value })}
                        placeholder="Description de l'image"
                      />
                    </div>
                    <div>
                      <Label>Catégorie</Label>
                      <Select
                        value={portfolioForm.categorie}
                        onValueChange={(v) => setPortfolioForm({ ...portfolioForm, categorie: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Fine Line">Fine Line</SelectItem>
                          <SelectItem value="Botanical">Botanical</SelectItem>
                          <SelectItem value="Ornamental">Ornamental</SelectItem>
                          <SelectItem value="Geometric">Geometric</SelectItem>
                          <SelectItem value="Minimal">Minimal</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full">Ajouter au portfolio</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Utilisez les flèches pour réorganiser l'ordre d'affichage des images.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {portfolio.map((img, index) => (
                <div key={img.id} className="relative group border border-border/40 p-2">
                  <img
                    src={img.image_url}
                    alt={img.titre || "Portfolio"}
                    className="w-full aspect-square object-cover"
                    onError={(e) => { e.target.src = 'https://placehold.co/400x400?text=Image+manquante'; }}
                  />
                  <div className="absolute top-4 right-4 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="icon"
                      variant="secondary"
                      className="h-8 w-8"
                      onClick={() => movePortfolioItem(index, 'up')}
                      disabled={index === 0}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="secondary"
                      className="h-8 w-8"
                      onClick={() => movePortfolioItem(index, 'down')}
                      disabled={index === portfolio.length - 1}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="destructive"
                      className="h-8 w-8"
                      onClick={() => handleDeletePortfolio(img.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="mt-2">
                    {img.titre && <p className="text-xs font-medium truncate">{img.titre}</p>}
                    {img.categorie && <p className="text-xs text-muted-foreground">{img.categorie}</p>}
                    <p className="text-xs text-muted-foreground">Position: {index + 1}</p>
                  </div>
                </div>
              ))}
            </div>

            {portfolio.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                Aucune image dans le portfolio. Cliquez sur "Ajouter une image" pour commencer.
              </div>
            )}
          </TabsContent>

          {/* ==================== PRODUCTS TAB ==================== */}
          <TabsContent value="products">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-heading">Gestion du Shop ({products.length})</h2>
              <Dialog open={newProductDialog} onOpenChange={setNewProductDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau Produit
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Créer un nouveau produit</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateProduct} className="space-y-4 mt-4">
                    <div>
                      <Label>Nom du produit</Label>
                      <Input
                        value={productForm.nom}
                        onChange={(e) => setProductForm({ ...productForm, nom: e.target.value })}
                        required
                        placeholder="Ex: Print A5 BLOOM"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                        required
                        placeholder="Description du produit..."
                      />
                    </div>
                    <div>
                      <Label>URL de l'image</Label>
                      <Input
                        value={productForm.image_url}
                        onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                        required
                        placeholder="https://..."
                      />
                      {productForm.image_url && (
                        <img src={productForm.image_url} alt="Preview" className="mt-2 h-32 object-cover border" />
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Prix (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={productForm.prix}
                          onChange={(e) => setProductForm({ ...productForm, prix: e.target.value })}
                          required
                          min="0"
                        />
                      </div>
                      <div>
                        <Label>Stock</Label>
                        <Input
                          type="number"
                          value={productForm.stock}
                          onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                          required
                          min="0"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Catégorie</Label>
                      <Select
                        value={productForm.categorie}
                        onValueChange={(v) => setProductForm({ ...productForm, categorie: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="prints">Prints</SelectItem>
                          <SelectItem value="stickers">Stickers</SelectItem>
                          <SelectItem value="other">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full">Créer le produit</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((product) => (
                <div key={product.id} className="border border-border/40 p-4 bg-card" data-testid={`product-item-${product.id}`}>
                  <img
                    src={product.image_url}
                    alt={product.nom}
                    className="w-full h-40 object-cover mb-4"
                    onError={(e) => { e.target.src = 'https://placehold.co/400x400?text=Image+manquante'; }}
                  />
                  <h3 className="font-medium">{product.nom}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
                  <p className="text-sm font-medium mt-2">{product.prix}€ - Stock: {product.stock}</p>
                  <p className="text-xs text-muted-foreground">Catégorie: {product.categorie}</p>
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingProduct(product)}
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteProduct(product.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {products.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                Aucun produit. Cliquez sur "Nouveau Produit" pour en ajouter.
              </div>
            )}

            {/* Edit Product Dialog */}
            <Dialog open={!!editingProduct} onOpenChange={() => setEditingProduct(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Modifier le produit</DialogTitle>
                </DialogHeader>
                {editingProduct && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Nom</Label>
                      <Input
                        value={editingProduct.nom}
                        onChange={(e) => setEditingProduct({ ...editingProduct, nom: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={editingProduct.description}
                        onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>URL de l'image</Label>
                      <Input
                        value={editingProduct.image_url}
                        onChange={(e) => setEditingProduct({ ...editingProduct, image_url: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Prix (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editingProduct.prix}
                          onChange={(e) => setEditingProduct({ ...editingProduct, prix: parseFloat(e.target.value) })}
                        />
                      </div>
                      <div>
                        <Label>Stock</Label>
                        <Input
                          type="number"
                          value={editingProduct.stock}
                          onChange={(e) => setEditingProduct({ ...editingProduct, stock: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                    <Button onClick={() => handleUpdateProduct(editingProduct)} className="w-full">
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ==================== SITE CONTENT TAB ==================== */}
          <TabsContent value="content">
            <div className="mb-6">
              <h2 className="text-lg font-heading">Textes du site</h2>
              <p className="text-sm text-muted-foreground">
                Modifiez les titres, sous-titres et descriptions de chaque page.
              </p>
            </div>

            <div className="space-y-4">
              {siteContent.map((content) => (
                <div key={content.id} className="border border-border/40 p-4 bg-card" data-testid={`content-item-${content.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-1 bg-secondary text-secondary-foreground">
                          {getPageLabel(content.page)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {getSectionLabel(content.section)}
                        </span>
                      </div>
                      {content.title && <h3 className="font-medium">{content.title}</h3>}
                      {content.subtitle && <p className="text-sm text-muted-foreground">{content.subtitle}</p>}
                      {content.content && (
                        <p className="text-sm mt-2 line-clamp-2">{content.content}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingContent(content)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {siteContent.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                Chargement des textes du site...
              </div>
            )}

            {/* Edit Content Dialog */}
            <Dialog open={!!editingContent} onOpenChange={() => setEditingContent(null)}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Modifier le texte</DialogTitle>
                </DialogHeader>
                {editingContent && (
                  <div className="space-y-4 mt-4">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm px-2 py-1 bg-secondary text-secondary-foreground">
                        {getPageLabel(editingContent.page)}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {getSectionLabel(editingContent.section)}
                      </span>
                    </div>
                    <div>
                      <Label>Titre</Label>
                      <Input
                        value={editingContent.title || ""}
                        onChange={(e) => setEditingContent({ ...editingContent, title: e.target.value })}
                        placeholder="Titre principal"
                      />
                    </div>
                    <div>
                      <Label>Sous-titre</Label>
                      <Input
                        value={editingContent.subtitle || ""}
                        onChange={(e) => setEditingContent({ ...editingContent, subtitle: e.target.value })}
                        placeholder="Sous-titre (optionnel)"
                      />
                    </div>
                    <div>
                      <Label>Contenu / Description</Label>
                      <Textarea
                        value={editingContent.content || ""}
                        onChange={(e) => setEditingContent({ ...editingContent, content: e.target.value })}
                        placeholder="Texte descriptif..."
                        className="min-h-[120px]"
                      />
                    </div>
                    <Button onClick={() => handleUpdateContent(editingContent)} className="w-full">
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer les modifications
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ==================== FAQ TAB ==================== */}
          <TabsContent value="faq">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-heading">Gestion de la FAQ ({faq.length})</h2>
              <Dialog open={newFaqDialog} onOpenChange={setNewFaqDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-faq-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle question
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Ajouter une question</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateFaq} className="space-y-4 mt-4">
                    <div>
                      <Label>Question</Label>
                      <Input
                        value={faqForm.question}
                        onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                        required
                        placeholder="Ex: Quel est le tarif minimum ?"
                      />
                    </div>
                    <div>
                      <Label>Réponse</Label>
                      <Textarea
                        value={faqForm.answer}
                        onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                        required
                        placeholder="Votre réponse détaillée..."
                        className="min-h-[120px]"
                      />
                    </div>
                    <Button type="submit" className="w-full">Ajouter la question</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Utilisez les flèches pour réorganiser l'ordre des questions.
            </p>

            <div className="space-y-3">
              {faq.map((item, index) => (
                <div key={item.id} className="border border-border/40 p-4 bg-card" data-testid={`faq-item-${item.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => moveFaqItem(index, 'up')}
                        disabled={index === 0}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => moveFaqItem(index, 'down')}
                        disabled={index === faq.length - 1}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Question {index + 1}</p>
                          <h3 className="font-medium">{item.question}</h3>
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{item.answer}</p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingFaq(item)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteFaq(item.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {faq.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                Aucune question. Cliquez sur "Nouvelle question" pour en ajouter.
              </div>
            )}

            {/* Edit FAQ Dialog */}
            <Dialog open={!!editingFaq} onOpenChange={() => setEditingFaq(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Modifier la question</DialogTitle>
                </DialogHeader>
                {editingFaq && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Question</Label>
                      <Input
                        value={editingFaq.question}
                        onChange={(e) => setEditingFaq({ ...editingFaq, question: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Réponse</Label>
                      <Textarea
                        value={editingFaq.answer}
                        onChange={(e) => setEditingFaq({ ...editingFaq, answer: e.target.value })}
                        className="min-h-[120px]"
                      />
                    </div>
                    <Button onClick={() => handleUpdateFaq(editingFaq)} className="w-full">
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
